import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { ThemeToggle } from '../context/ThemeContext';
import BrandLogo from './BrandLogo';

const Navbar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Products', href: '#products' },
    { name: 'Enterprise', href: '#enterprise' },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Developers', href: '/docs', isRoute: true },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed w-full z-50 top-0 transition-all duration-500
        ${scrolled
          ? 'bg-app/80 backdrop-blur-2xl border-b border-line py-3'
          : 'bg-transparent py-6'
        }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-8">
        <a href="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <BrandLogo className="h-9 md:h-10 transition-transform duration-300 group-hover:scale-[1.03]" />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.isRoute ? (
              <button
                key={link.name}
                onClick={() => navigate(link.href)}
                className="text-sm text-content-2 hover:text-content transition-colors duration-300"
                data-testid={`nav-item-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className="text-sm text-content-2 hover:text-content transition-colors duration-300"
                data-testid={`nav-item-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            )
          )}
        </div>

        <div className="hidden md:flex items-center gap-2">
          <ThemeToggle />
          <button
            onClick={() => navigate('/auth')}
            className="px-3 py-2 text-sm text-content-2 hover:text-content transition-colors duration-300"
            data-testid="nav-signin-btn"
          >
            Sign in
          </button>
          <button
            onClick={() => navigate('/chat')}
            className="group flex items-center gap-2 bg-btn text-btn-foreground px-5 py-2.5 rounded-full text-sm font-medium hover:opacity-90 transition-opacity duration-300 active:scale-[0.97] ml-2"
            data-testid="nav-get-started-btn"
          >
            Get Started
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        <div className="md:hidden flex items-center gap-1">
          <ThemeToggle />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-content-2 hover:text-content transition-colors"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className={`md:hidden transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="mx-4 mt-3 p-4 bg-surface/95 backdrop-blur-2xl border border-line rounded-2xl space-y-1">
          {navLinks.map((link) =>
            link.isRoute ? (
              <button
                key={link.name}
                onClick={() => { setIsOpen(false); navigate(link.href); }}
                className="block w-full text-left px-4 py-3 text-sm text-content-2 hover:text-content hover:bg-overlay-hover rounded-lg transition-colors"
              >
                {link.name}
              </button>
            ) : (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-sm text-content-2 hover:text-content hover:bg-overlay-hover rounded-lg transition-colors"
              >
                {link.name}
              </a>
            )
          )}
          <div className="pt-3 border-t border-line flex gap-2">
            <button
              onClick={() => { setIsOpen(false); navigate('/auth'); }}
              className="flex-1 py-3 text-content-2 text-sm hover:text-content transition-colors"
            >
              Sign in
            </button>
            <button
              onClick={() => { setIsOpen(false); navigate('/chat'); }}
              className="flex-1 py-3 bg-btn text-btn-foreground rounded-full font-semibold text-sm"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
